import * as React from 'react';
import { Card, CardContent } from '../components/primitives';

export interface AuthLayoutProps {
  title: string;
  subtitle?: React.ReactNode;
  /** Rendered above the form card (e.g. an error alert). */
  banner?: React.ReactNode;
  /** Rendered below the card (e.g. secondary links). */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * Shared shell for the pre-login screens: a heading and a single card holding
 * the form.
 *
 * No logo. Every one of these screens carried the shop's mark at 240px above
 * the heading, and review asked for it to go — "all these places where we've
 * got the logo set in the middle of the page, just get rid of that". It was
 * the largest thing on a screen whose job is one short form, and a seller
 * following an emailed link already knows whose marketplace they are joining.
 *
 * The ground is the app's surface, not white: a white card on a white page has
 * no card. Every pre-login screen sits on the same ground now — some were white
 * and some were grey, which is why they didn't look like one product.
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({ title, subtitle, banner, footer, children }) => (
  <div className="flex min-h-screen flex-col items-center justify-center bg-surface px-4 py-12">
    <div className="w-full max-w-md">
      <div className="flex flex-col items-center text-center">
        {/* h2, not h1: the e2e page objects (PartnerLogin/PartnerTermsPage) assert
            page.locator('h2') for the screen title — the legacy AuthHeader used h2. */}
        <h2 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h2>
        {subtitle && <p className="mt-2 text-sm text-muted-foreground">{subtitle}</p>}
      </div>

      {banner && <div className="mt-6">{banner}</div>}

      <Card className="mt-6">
        <CardContent className="p-6 sm:p-8">{children}</CardContent>
      </Card>

      {footer && <div className="mt-6 text-center text-sm text-muted-foreground">{footer}</div>}
    </div>
  </div>
);
